import React from "react"
import { FaSearch, FaDownload } from "react-icons/fa"
import { Autocomplete, Button, FormInput, FormLabel } from "../../../components/ui"
import { useStaffAutocomplete } from "../../../hooks/staff/useStaffAutocomplete"
import { useDebounce } from "../../../hooks/useDebounce"
import { getInitialDate, toISODateInput } from "../../../helper/formatDate"

/**
 * @typedef {Object} ReturFilterData
 * @property {import("../../../components/ui/autocomplete").AutocompleteOption} staff
 * @property {{ start: string, end: string }} [dateRange]
 */

/**
 * @typedef {Object} ReturFilterProps
 * @property {(data: ReturFilterData | null) => void} onFilter
 * 
 * @param {ReturFilterProps} props
 */
export default function ReturFilter({ onFilter }) {
  const [staffInput, setStaffInput] = React.useState("")
  const [dateRangeInput, setDateRangeInput] = React.useState({
    start: "",
    end: "",
  })
  const [selectedStaffSuggestion, setSelectedStaffSuggestion] = React.useState(null)

  const debouncedStaffInput = useDebounce(staffInput)
  const { data: staffData } = useStaffAutocomplete(String(debouncedStaffInput) !== selectedStaffSuggestion?.label ? String(debouncedStaffInput).toLowerCase() : "")

  const suggestionOptions = React.useMemo(() => {
    if (!staffInput) return []
    if (staffInput === selectedStaffSuggestion?.label) return []
    return staffData.map((item) => ({ id: item.staff_id, label: item.staff_name }))
  }, [staffData, staffInput, selectedStaffSuggestion])

  const handleSubmitFilter = (evt) => {
    evt.preventDefault()
    if (typeof onFilter !== "function") return
    onFilter({
      staff: selectedStaffSuggestion,
      dateRange: dateRangeInput,
    })
  }

  return (
    <div className="mb-4 py-2">
      <div className="flex items-end space-x-4">
        <div className="flex-1" style={{ position: 'relative' }}>
          <FormLabel htmlFor="staff-input">Nama Staff:</FormLabel>
          <FormInput
            id="staff-input"
            placeholder="Masukkan nama staff..."
            value={staffInput}
            onChange={(evt) => {
              setStaffInput(evt.target.value)
              setSelectedStaffSuggestion(null)
            }}
            style={{ marginBottom: 0 }}
            autoComplete="off"
          />
          <Autocomplete
            value={selectedStaffSuggestion}
            options={suggestionOptions}
            onChange={(value) => {
              setStaffInput(value.label)
              setSelectedStaffSuggestion(value)
            }}
          />
        </div>
        <div className="flex-none flex items-center space-x-4">
          <div className="flex-none" style={{ width: "250px" }}>
            <FormLabel htmlFor="transaction-date">Tanggal Transaksi (Mulai):</FormLabel>
            <FormInput
              id="transaction-date"
              type="date"
              value={dateRangeInput.start}
              max={toISODateInput(getInitialDate('daily'))}
              onChange={(evt) => {
                setDateRangeInput((prevState) => ({ ...prevState, start: evt.target.value }))
              }}
            />
          </div>
          <div className="flex-none" style={{ width: "250px" }}>
            <FormLabel htmlFor="transaction-date">Tanggal Transaksi (Akhir):</FormLabel>
            <FormInput
              id="transaction-date"
              type="date"
              value={dateRangeInput.end}
              max={toISODateInput(getInitialDate('daily'))}
              onChange={(evt) => {
                setDateRangeInput((prevState) => ({ ...prevState, end: evt.target.value }))
              }}
            />
          </div>
        </div>
        <Button variant="primary" onClick={handleSubmitFilter}>
          <FaSearch className="mr-2" /> Filter
        </Button>
        <Button variant="info">
          <FaDownload className="mr-2" /> Download Excel
        </Button>
      </div>
    </div>
  )
}
